import { computed } from 'vue'
import { useStore } from 'vuex'
import workflowApi from '@/api/workflowApi'
import { SchemaType } from '@/constants/schema'
import safeStructuredClone from '@/utils/clone'
import { cardHelpers } from '@/mixins/cardHelpers'
import { formatCardKey } from '@/ai/utils/helpers'
import { DomainModelRole } from '@/constants/cardTypes'
import { validateWorkflow } from '@/utils/domainModelValidator'
import { buildCreateEventMutation } from '@/shared/utils/createEventMutation'
import { buildUpdateEventMutation } from '@/shared/utils/updateEventMutation'
import { buildDeleteEventMutation } from '@/shared/utils/deleteEventMutation'
import { buildAddConnectionMutation, buildRemoveConnectionMutation } from '@/shared/utils/connectionMutations'
import {
  buildCreateLaneMutation,
  buildUpdateLaneMutation,
  buildDeleteLaneMutation
} from '@/shared/utils/laneMutations'
import {
  buildCreateGroupMutation,
  buildUpdateGroupMutation,
  buildDeleteGroupMutation
} from '@/shared/utils/groupMutations'
import {
  buildCreateCardMutation,
  buildUpdateCardMutation,
  buildDeleteCardMutation
} from '@/shared/utils/cardMutations'
import {
  buildCreateEntitiesMutation,
  buildUpdateEntitiesMutation,
  buildDeleteEntityMutation
} from '@/shared/utils/entityMutations'
import {
  buildCreateCommandsMutation,
  buildUpdateCommandsMutation,
  buildDeleteCommandMutation
} from '@/shared/utils/commandMutations'
import {
  buildCreateReadModelsMutation,
  buildUpdateReadModelsMutation,
  buildDeleteReadModelMutation
} from '@/shared/utils/readModelMutations'
import {
  buildCreateDomainEventSchemasMutation,
  buildUpdateDomainEventSchemasMutation,
  buildDeleteDomainEventSchemaMutation
} from '@/shared/utils/domainEventSchemaMutations'
import {
  buildCreateBoundedContextMutation,
  buildUpdateBoundedContextMutation,
  buildDeleteBoundedContextMutation
} from '@/shared/utils/boundedContextMutations'
import {
  parseEntityResponse,
  parseCommandFields,
  parseReadModelFields,
  assignBoundedContexts
} from '@/shared/utils/workflowSpec'
import { getLastCardSortkey } from '@/shared/utils/sortkey'
import { buildUpdateEventCardOperations } from '@/shared/utils/eventCardMutations'
import {
  generateCardsFromDescription,
  generateEntitiesFromDescription,
  generateEntitiesFromWorkflow,
  generateReadModelsFromDescription,
  generateWorkflowCards,
  generateWorkflowDiagram,
  generateWorkflowReadModels,
  generateWorkflowCommands,
  generateCommandsFromDescription
} from '@/ai'

export const functionType = {
  UPDATE_WORKFLOW_NAME: 'updateWorkflowName',
  CREATE_EVENT: 'create_domain_event',
  UPDATE_EVENT: 'update_domain_event',
  DELETE_EVENT: 'delete_domain_event',
  ADD_CONNECTION: 'add_connection',
  REMOVE_CONNECTION: 'remove_connection',
  CREATE_LANE: 'create_lane',
  UPDATE_LANE: 'update_lane',
  DELETE_LANE: 'delete_lane',
  CREATE_GROUP: 'create_group',
  UPDATE_GROUP: 'update_group',
  DELETE_GROUP: 'delete_group',
  CREATE_CARD: 'create_card',
  DELETE_CARD: 'delete_card',
  UPDATE_CARD: 'update_card',
  CREATE_ENTITY: 'create_entities',
  UPDATE_ENTITY: 'update_entities',
  DELETE_ENTITY: 'delete_entity',
  CREATE_COMMAND: 'create_commands',
  UPDATE_COMMAND: 'update_commands',
  DELETE_COMMAND: 'delete_command',
  CREATE_DOMAIN_EVENT_SCHEMA: 'create_domain_event_schemas',
  UPDATE_DOMAIN_EVENT_SCHEMA: 'update_domain_event_schemas',
  DELETE_DOMAIN_EVENT_SCHEMA: 'delete_domain_event_schema',
  CREATE_READ_MODEL: 'create_read_models',
  UPDATE_READ_MODEL: 'update_read_models',
  DELETE_READ_MODEL: 'delete_read_model',
  CREATE_BOUNDED_CONTEXT: 'create_bounded_context',
  UPDATE_BOUNDED_CONTEXT: 'update_bounded_context',
  DELETE_BOUNDED_CONTEXT: 'delete_bounded_context',
  REGENERATE_CARDS: 'regenerateCards',
  ADD_MISSING_CARDS: 'addMissingCards',
  REGENERATE_COMMANDS: 'regenerateCommands',
  ADD_MISSING_COMMANDS: 'addMissingCommands',
  REGENERATE_READ_MODELS: 'regenerateReadModels',
  ADD_MISSING_READ_MODELS: 'addMissingReadModels',
  REGENERATE_ENTITIES: 'regenerateEntities',
  ADD_MISSING_ENTITIES: 'addMissingEntities',
  GENERATE_ENTITIES_FROM_DESCRIPTION: 'generateEntitiesFromDescription',
  GENERATE_COMMANDS_FROM_DESCRIPTION: 'generateCommandsFromDescription',
  GENERATE_READ_MODELS_FROM_DESCRIPTION: 'generateReadModelsFromDescription',
  GENERATE_CARDS_FROM_DESCRIPTION: 'generateCardsFromDescription',
  PLAN_WORKFLOW_WITH_AI: 'planWorkflowWithAI',
  VALIDATE_DOMAIN_MODEL: 'validateDomainModel',
  ANALYZE_DOMAIN_MODEL: 'analyzeDomainModel',
  ROLLBACK_WORKFLOW: 'rollbackWorkflow'
}

export const useWorkflowAssistantFunctions = () => {
  const store = useStore()

  const workflow = computed(() => store.state.workflow.workflow)
  const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])
  const svgLanes = computed(() => store.getters['svgGraphData/svgLanesData'])
  const svgGroups = computed(() => store.getters['svgGraphData/svgGroupsData'])
  const cardTypes = computed(() => store.state.cardTypes.cardTypes)

  const findEventByResponse = (data) => {
    const eventName = data.domainEventName || data.name
    const lane = data.role
      ? Object.values(svgLanes.value).find((l) => l.name === data.role)
      : null

    if (lane) {
      return workflow.value.domainEvents.find((e) => e.description === eventName && e.laneId === lane.id)
    }

    return workflow.value.domainEvents.find((e) => e.description === eventName)
  }

  const updateWorkflowName = async (args) => {
    const { name } = args

    return await workflowApi.updateWorkflowName({
      ...workflow.value,
      name,
      updateNote: {
        action: 'changed workflow name to',
        target: name
      }
    })
  }

  const createEvent = async (args) => {
    const built = buildCreateEventMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      args,
      cardTypes: cardTypes.value,
      lastCardSortkey: getLastCardSortkey(workflow.value.domainEvents || []),
      settleOffsets: true
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    // The new event is appended, so its post-mutation index is the pre-mutation length.
    const eventIndex = (workflow.value.domainEvents || []).length

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)

      if (built.pendingCards.length > 0) {
        await workflowApi.updateCardsWithInput({
          workflowId: workflow.value.id,
          expectedVersion: workflow.value.version,
          updateNote: { action: 'added cards', target: built.description },
          cards: built.pendingCards.map((card) => ({ ...card, cardIndex: -1, eventId: built.newEventId, eventIndex }))
        })
      }
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to create event' }
    }

    return { success: true }
  }

  const updateDomainEvent = async (args) => {
    const built = buildUpdateEventMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      eventRef: args.domainEvent,
      description: args.description,
      lane: args.lane,
      color: args.color,
      conditionLabel: args.conditionLabel,
      group: args.group,
      type: args.type
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    let cardOperations = { pendingNewCards: [], pendingCardDeletions: [], pendingCardUpdates: [] }
    if (args.aggregateRoot !== undefined || args.acceptanceCriteria !== undefined) {
      cardOperations = buildUpdateEventCardOperations({
        workflow: workflow.value,
        event: built.event,
        aggregateRoot: args.aggregateRoot,
        acceptanceCriteria: args.acceptanceCriteria,
        cardTypes: cardTypes.value
      })

      if (cardOperations.error) {
        return { success: false, error: cardOperations.error }
      }
    }

    const hasCardChanges = cardOperations.pendingCardDeletions.length > 0 ||
      cardOperations.pendingCardUpdates.length > 0 ||
      cardOperations.pendingNewCards.length > 0

    if (!built.mutationInput && !hasCardChanges) {
      return { success: false, error: 'No changes specified. Provide at least one of: description, lane, color, conditionLabel, group, type, aggregateRoot, acceptanceCriteria.' }
    }

    try {
      if (built.mutationInput) {
        await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
      }

      if (hasCardChanges) {
        const eventCards = built.event.cards || []
        const deletions = [...cardOperations.pendingCardDeletions]
        for (const deletion of deletions) {
          const freshCardIndex = eventCards.findIndex((card) => card.id === deletion.cardId)
          if (freshCardIndex !== -1) {
            deletion.cardIndex = freshCardIndex
          }
        }
        deletions.sort((a, b) => b.cardIndex - a.cardIndex)

        for (const deletion of deletions) {
          await workflowApi.deleteCardWithInput({
            workflowId: workflow.value.id,
            projectId: workflow.value.projectId,
            eventId: built.eventId,
            eventIndex: built.eventIndex,
            cardId: deletion.cardId,
            cardIndex: deletion.cardIndex,
            cardDescription: deletion.cardDescription,
            expectedVersion: workflow.value.version
          })
        }

        const updateCards = []
        for (const update of cardOperations.pendingCardUpdates) {
          const freshIndex = eventCards.findIndex((card) => card.id === update.cardId)
          if (freshIndex === -1) {
            continue
          }
          const deletionsBelow = deletions.filter((deletion) => deletion.cardIndex < freshIndex).length
          updateCards.push({
            cardId: update.cardId,
            cardIndex: freshIndex - deletionsBelow,
            expectedVersion: update.expectedVersion,
            description: update.description,
            sortkey: update.sortkey,
            cardTypeId: update.cardTypeId,
            done: update.done ?? false,
            schemaId: update.schemaId,
            boundedContext: update.boundedContext,
            releaseNo: update.releaseNo,
            eventId: built.eventId,
            eventIndex: built.eventIndex
          })
        }
        if (updateCards.length > 0) {
          await workflowApi.updateCardsWithInput({
            workflowId: workflow.value.id,
            expectedVersion: workflow.value.version,
            updateNote: { action: 'updated cards', target: built.event.description || built.eventId },
            cards: updateCards
          })
        }

        if (cardOperations.pendingNewCards.length > 0) {
          await workflowApi.updateCardsWithInput({
            workflowId: workflow.value.id,
            expectedVersion: workflow.value.version,
            updateNote: { action: 'added cards', target: built.event.description || built.eventId },
            cards: cardOperations.pendingNewCards.map((card) => ({ ...card, cardIndex: -1, eventId: built.eventId, eventIndex: built.eventIndex }))
          })
        }
      }
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to update event' }
    }

    return { success: true }
  }

  const deleteEvent = async (args) => {
    const built = buildDeleteEventMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      eventRef: args.domainEvent
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to delete event' }
    }

    return { success: true }
  }

  const addConnection = async (args) => {
    const built = buildAddConnectionMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      from: args.from,
      to: args.to,
      conditionLabel: args.conditionLabel
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to add connection' }
    }

    return { success: true }
  }

  const removeConnection = async (args) => {
    const built = buildRemoveConnectionMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      from: args.from,
      to: args.to
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to remove connection' }
    }

    return { success: true }
  }

  const createLane = async (args) => {
    const built = buildCreateLaneMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      name: args.name,
      position: args.position
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to create lane' }
    }

    return { success: true }
  }

  const updateLane = async (args) => {
    const built = buildUpdateLaneMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      lane: args.lane,
      name: args.name,
      position: args.position
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to rename lane' }
    }

    return { success: true }
  }

  const deleteLane = async (args) => {
    const built = buildDeleteLaneMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      lane: args.lane
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to delete lane' }
    }

    return { success: true }
  }

  const createGroup = async (args) => {
    const built = buildCreateGroupMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      name: args.name
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to create group' }
    }

    return { success: true }
  }

  const updateGroup = async (args) => {
    const built = buildUpdateGroupMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      group: args.group,
      name: args.name
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to rename group' }
    }

    return { success: true }
  }

  const deleteGroup = async (args) => {
    const built = buildDeleteGroupMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      group: args.group
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to delete group' }
    }

    return { success: true }
  }

  const createCard = async (args) => {
    const { domainEvent, description, cardTypeId, releaseNo, done } = args

    const built = buildCreateCardMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      domainEvent,
      description,
      cardTypeId,
      releaseNo,
      done,
      cardTypes: cardTypes.value
    })
    if (built.error) {
      return { success: false, error: built.error }
    }

    return await workflowApi.updateCardsWithInput(built.mutationInput)
  }

  const deleteCard = async (args) => {
    const { domainEvent, description } = args

    const built = buildDeleteCardMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      domainEvent,
      description
    })
    if (built.error) {
      return { success: false, error: built.error }
    }

    return await workflowApi.deleteCardWithInput({
      ...built.deleteInput,
      projectId: workflow.value.projectId
    })
  }

  const updateCard = async (args) => {
    const { domainEvent, currentDescription, description, cardTypeId, releaseNo, done, schema } = args

    const built = buildUpdateCardMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      domainEvent,
      currentDescription,
      description,
      cardTypeId,
      releaseNo,
      done,
      schema,
      cardTypes: cardTypes.value
    })
    if (built.error) {
      return { success: false, error: built.error }
    }

    return await workflowApi.updateCardsWithInput(built.mutationInput)
  }

  const createEntities = async (args) => {
    const built = buildCreateEntitiesMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      entities: args.entities,
      cardTypes: cardTypes.value
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.schemaInput)

      if (built.cardInput) {
        await workflowApi.updateCardsWithInput({ ...built.cardInput, expectedVersion: workflow.value.version })
      }
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to create entities' }
    }

    return { success: true }
  }

  const updateEntities = async (args) => {
    const built = buildUpdateEntitiesMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      entities: args.entities
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.schemaInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to update entities' }
    }

    return { success: true }
  }

  const deleteEntity = async (args) => {
    const built = buildDeleteEntityMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      entity: args.entity
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.schemaInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to delete entity' }
    }

    return { success: true }
  }

  const createCommands = async (args) => {
    const built = buildCreateCommandsMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      commands: args.commands,
      cardTypes: cardTypes.value
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.schemaInput)
      await workflowApi.updateCardsWithInput({ ...built.cardInput, expectedVersion: workflow.value.version })
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to create commands' }
    }

    return { success: true }
  }

  const deleteCommand = async (args) => {
    const built = buildDeleteCommandMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      command: args.command,
      cardTypes: cardTypes.value
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      if (built.cardDelete) {
        await workflowApi.deleteCardWithInput({
          ...built.cardDelete,
          projectId: workflow.value.projectId,
          expectedVersion: workflow.value.version
        })
      }
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, {
        ...built.schemaInput,
        expectedVersion: workflow.value.version
      })
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to delete command' }
    }

    return { success: true }
  }

  const updateCommands = async (args) => {
    const built = buildUpdateCommandsMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      commands: args.commands,
      cardTypes: cardTypes.value
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.schemaInput)
      if (built.cardInput) {
        await workflowApi.updateCardsWithInput({ ...built.cardInput, expectedVersion: workflow.value.version })
      }
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to update commands' }
    }

    return { success: true }
  }

  const createDomainEventSchemas = async (args) => {
    const built = buildCreateDomainEventSchemasMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      domainEventSchemas: args.domainEventSchemas,
      cardTypes: cardTypes.value
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.schemaInput)
      if (built.cardInput) {
        await workflowApi.updateCardsWithInput({ ...built.cardInput, expectedVersion: workflow.value.version })
      }
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to create domain event schemas' }
    }

    return { success: true }
  }

  const deleteDomainEventSchema = async (args) => {
    const built = buildDeleteDomainEventSchemaMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      domainEventSchema: args.domainEventSchema,
      cardTypes: cardTypes.value
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      if (built.cardDelete) {
        await workflowApi.deleteCardWithInput({
          ...built.cardDelete,
          projectId: workflow.value.projectId,
          expectedVersion: workflow.value.version
        })
      }
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, {
        ...built.schemaInput,
        expectedVersion: workflow.value.version
      })
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to delete domain event schema' }
    }

    return { success: true }
  }

  const updateDomainEventSchemas = async (args) => {
    const built = buildUpdateDomainEventSchemasMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      domainEventSchemas: args.domainEventSchemas,
      cardTypes: cardTypes.value
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.schemaInput)
      if (built.cardInput) {
        await workflowApi.updateCardsWithInput({ ...built.cardInput, expectedVersion: workflow.value.version })
      }
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to update domain event schemas' }
    }

    return { success: true }
  }

  const createReadModels = async (args) => {
    const built = buildCreateReadModelsMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      readModels: args.readModels,
      cardTypes: cardTypes.value
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      if (built.schemaInput) {
        await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.schemaInput)
      }
      await workflowApi.updateCardsWithInput({ ...built.cardInput, expectedVersion: workflow.value.version })
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to create read models' }
    }

    return { success: true }
  }

  const deleteReadModel = async (args) => {
    const built = buildDeleteReadModelMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      readModel: args.readModel,
      cardTypes: cardTypes.value
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      for (const cardDelete of built.cardDeletes) {
        await workflowApi.deleteCardWithInput({
          ...cardDelete,
          projectId: workflow.value.projectId,
          expectedVersion: workflow.value.version
        })
      }
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, {
        ...built.schemaInput,
        expectedVersion: workflow.value.version
      })
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to delete read model' }
    }

    return { success: true }
  }

  const updateReadModels = async (args) => {
    const built = buildUpdateReadModelsMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      readModels: args.readModels,
      cardTypes: cardTypes.value
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.schemaInput)
      if (built.cardInput) {
        await workflowApi.updateCardsWithInput({ ...built.cardInput, expectedVersion: workflow.value.version })
      }
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to update read models' }
    }

    return { success: true }
  }

  const createBoundedContext = async (args) => {
    const built = buildCreateBoundedContextMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      name: args.name
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to create bounded context' }
    }

    return { success: true }
  }

  const updateBoundedContext = async (args) => {
    const built = buildUpdateBoundedContextMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      boundedContext: args.boundedContext,
      name: args.name
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to rename bounded context' }
    }

    return { success: true }
  }

  const deleteBoundedContext = async (args) => {
    const built = buildDeleteBoundedContextMutation({
      workflow: workflow.value,
      workflowId: workflow.value.id,
      boundedContext: args.boundedContext
    })

    if (built.error) {
      return { success: false, error: built.error }
    }

    try {
      await store.dispatch('boundedContexts/deleteBoundedContext', built.boundedContextId)
      await workflowApi.updateWorkflowMutation(workflow.value.projectId, built.mutationInput)
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to delete bounded context' }
    }

    return { success: true }
  }

  const regenerateCards = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const enabledCardTypes = cardTypes.value.filter((ct) => ct.generateWithAI)
    if (!enabledCardTypes.length) {
      return { success: false, error: 'No card types have AI generation enabled' }
    }

    const enabledCardTypeIds = new Set(enabledCardTypes.map((ct) => ct.id))
    let totalDeleted = 0

    for (const event of workflow.value.domainEvents || []) {
      const eventCards = svgGraph.value[event.id]?.cards || []
      const cardsToDelete = eventCards.filter((card) => enabledCardTypeIds.has(card.cardTypeId))

      for (const card of cardsToDelete) {
        await workflowApi.deleteCard(workflow.value.id, { cardId: card.id })
        totalDeleted++
      }
    }

    const response = await generateWorkflowCards({
      workflow: workflow.value,
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: true, deleted: totalDeleted, created: 0 }
    }

    let totalCreated = 0

    for (const cardData of events) {
      const event = findEventByResponse(cardData)
      if (!event) {
        continue
      }

      for (const cardType of enabledCardTypes) {
        const cardTypeKey = formatCardKey(cardType.title)
        let cardValues = cardData[cardTypeKey]

        if (!cardValues) {
          continue
        }

        if (!Array.isArray(cardValues)) {
          cardValues = [cardValues]
        }

        for (const cardValue of cardValues) {
          const cardText = typeof cardValue === 'object' ? cardValue.name : cardValue
          if (!cardText) {
            continue
          }

          await workflowApi.createCard(workflow.value.id, event.id, {
            cardTypeId: cardType.id,
            description: cardText
          })
          totalCreated++
        }
      }
    }

    return { success: true, deleted: totalDeleted, created: totalCreated }
  }

  const addMissingCards = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const enabledCardTypes = cardTypes.value.filter((ct) => ct.generateWithAI)
    if (!enabledCardTypes.length) {
      return { success: false, error: 'No card types have AI generation enabled' }
    }

    const response = await generateWorkflowCards({
      workflow: workflow.value,
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate cards' }
    }

    let totalCreated = 0

    for (const cardData of events) {
      const event = findEventByResponse(cardData)
      if (!event) {
        continue
      }

      const eventCards = svgGraph.value[event.id]?.cards || []
      const existingCardTypeIds = new Set(eventCards.map((card) => card.cardTypeId))

      for (const cardType of enabledCardTypes) {
        if (existingCardTypeIds.has(cardType.id)) {
          continue
        }

        const cardTypeKey = formatCardKey(cardType.title)
        let cardValues = cardData[cardTypeKey]

        if (!cardValues) {
          continue
        }

        if (!Array.isArray(cardValues)) {
          cardValues = [cardValues]
        }

        for (const cardValue of cardValues) {
          const cardText = typeof cardValue === 'object' ? cardValue.name : cardValue
          if (!cardText) {
            continue
          }

          await workflowApi.createCard(workflow.value.id, event.id, {
            cardTypeId: cardType.id,
            description: cardText
          })
          totalCreated++
        }
      }
    }

    return { success: true, created: totalCreated }
  }

  const regenerateCommandSchemas = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const commandCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.COMMAND)
    if (!commandCardType) {
      return { success: false, error: 'No command card type found' }
    }

    const commandSchemaIds = new Set()
    const existingSchemas = workflow.value.schemas || []
    for (const schema of existingSchemas) {
      if (schema.type === SchemaType.COMMAND) {
        commandSchemaIds.add(schema.id)
      }
    }

    const schemasWithoutCommands = existingSchemas.filter((s) => s.type !== SchemaType.COMMAND)
    const totalDeleted = commandSchemaIds.size

    if (totalDeleted > 0) {
      const currentSvgGraph = safeStructuredClone(svgGraph.value)

      for (const eventId of Object.keys(currentSvgGraph)) {
        const cards = currentSvgGraph[eventId]?.cards || []
        for (const card of cards) {
          if (card.schemaId && commandSchemaIds.has(card.schemaId)) {
            delete card.schemaId
          }
        }
      }

      await workflowApi.updateWorkflow({
        svgGraph: currentSvgGraph,
        svgLanes: svgLanes.value,
        svgGroups: svgGroups.value,
        schemas: schemasWithoutCommands,
        workflow: workflow.value,
        updateNote: {
          action: 'deleted command schemas',
          target: `${totalDeleted} schemas`
        }
      })
    }

    const response = await generateWorkflowCommands({
      workflow: { ...workflow.value, schemas: schemasWithoutCommands },
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: true, deleted: totalDeleted, created: 0 }
    }

    let totalCreated = 0

    for (const eventData of events) {
      const event = findEventByResponse(eventData)
      if (!event) {
        continue
      }

      const commandCard = cardHelpers.methods.getCommandCard(event)

      if (!commandCard || !eventData.fields?.length) {
        continue
      }

      const fields = parseCommandFields(eventData)

      const newSchema = {
        name: SchemaType.COMMAND,
        type: SchemaType.COMMAND,
        fields
      }

      await workflowApi.addSchemaAndLinkCard({
        schema: newSchema,
        cardId: commandCard.id,
        eventId: event.id,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })

      totalCreated++
    }

    return { success: true, deleted: totalDeleted, created: totalCreated }
  }

  const addMissingCommandSchemas = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const commandCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.COMMAND)
    if (!commandCardType) {
      return { success: false, error: 'No command card type found' }
    }

    const response = await generateWorkflowCommands({
      workflow: workflow.value,
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate commands' }
    }

    let totalCreated = 0

    for (const eventData of events) {
      const event = findEventByResponse(eventData)
      if (!event) {
        continue
      }

      const commandCard = cardHelpers.methods.getCommandCard(event)
      if (!commandCard || commandCard.schemaId || !eventData.fields?.length) {
        continue
      }

      const fields = parseCommandFields(eventData)

      const newSchema = {
        name: SchemaType.COMMAND,
        type: SchemaType.COMMAND,
        fields
      }

      await workflowApi.addSchemaAndLinkCard({
        schema: newSchema,
        cardId: commandCard.id,
        eventId: event.id,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })

      totalCreated++
    }

    return { success: true, created: totalCreated }
  }

  const regenerateReadModelSchemas = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const readModelCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.READ_MODEL)
    if (!readModelCardType) {
      return { success: false, error: 'No read model card type found' }
    }

    const querySchemaIds = new Set()
    const existingSchemas = workflow.value.schemas || []
    for (const schema of existingSchemas) {
      if (schema.type === SchemaType.QUERY) {
        querySchemaIds.add(schema.id)
      }
    }

    const schemasWithoutQueries = existingSchemas.filter((s) => s.type !== SchemaType.QUERY)
    const totalDeleted = querySchemaIds.size

    if (totalDeleted > 0) {
      const currentSvgGraph = safeStructuredClone(svgGraph.value)

      for (const eventId of Object.keys(currentSvgGraph)) {
        const cards = currentSvgGraph[eventId]?.cards || []
        for (const card of cards) {
          if (card.schemaId && querySchemaIds.has(card.schemaId)) {
            delete card.schemaId
          }
        }
      }

      await workflowApi.updateWorkflow({
        svgGraph: currentSvgGraph,
        svgLanes: svgLanes.value,
        svgGroups: svgGroups.value,
        schemas: schemasWithoutQueries,
        workflow: workflow.value,
        updateNote: {
          action: 'deleted read model schemas',
          target: `${totalDeleted} schemas`
        }
      })
    }

    const response = await generateWorkflowReadModels({
      workflow: { ...workflow.value, schemas: schemasWithoutQueries },
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: true, deleted: totalDeleted, created: 0 }
    }

    let totalCreated = 0

    for (const eventData of events) {
      const event = findEventByResponse(eventData)
      if (!event || !eventData.readModelFields?.length) {
        continue
      }

      const eventCards = svgGraph.value[event.id]?.cards || []
      const queryCard = eventCards.find((c) => c.cardTypeId === readModelCardType.id)

      const fields = parseReadModelFields(eventData.readModelFields)

      const entities = workflow.value.schemas?.filter((s) => s.type === SchemaType.ENTITY) || []
      const queriedEntity = eventData.queriedEntity
        ? entities.find((e) => e.name.toLowerCase() === eventData.queriedEntity.toLowerCase())
        : null

      const newSchema = {
        name: SchemaType.QUERY,
        type: SchemaType.QUERY,
        cardinality: eventData.cardinality || 'one-to-many',
        fields,
        entityId: queriedEntity?.id || null
      }
      if (eventData.readModelDescription) {
        newSchema.description = eventData.readModelDescription
      }

      await workflowApi.addSchemaAndLinkCard({
        schema: newSchema,
        cardId: queryCard?.id || null,
        eventId: event.id,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })

      totalCreated++
    }

    return { success: true, deleted: totalDeleted, created: totalCreated }
  }

  const addMissingReadModelSchemas = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const readModelCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.READ_MODEL)
    if (!readModelCardType) {
      return { success: false, error: 'No read model card type found' }
    }

    const response = await generateWorkflowReadModels({
      workflow: workflow.value,
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate read models' }
    }

    let totalCreated = 0

    for (const eventData of events) {
      const event = findEventByResponse(eventData)
      if (!event || !eventData.readModelFields?.length) {
        continue
      }

      const queryCards = cardHelpers.methods.getQueryCards(event)
      const cardsWithoutSchema = queryCards.filter((card) => !card.schemaId)

      if (!cardsWithoutSchema.length) {
        continue
      }

      const fields = parseReadModelFields(eventData.readModelFields)

      const entities = workflow.value.schemas?.filter((s) => s.type === SchemaType.ENTITY) || []
      const queriedEntity = eventData.queriedEntity
        ? entities.find((e) => e.name.toLowerCase() === eventData.queriedEntity.toLowerCase())
        : null

      for (const queryCard of cardsWithoutSchema) {
        const newSchema = {
          name: SchemaType.QUERY,
          type: SchemaType.QUERY,
          cardinality: eventData.cardinality || 'one-to-many',
          fields,
          entityId: queriedEntity?.id || null
        }

        await workflowApi.addSchemaAndLinkCard({
          schema: newSchema,
          cardId: queryCard.id,
          eventId: event.id,
          workflow: workflow.value,
          svgGraph: svgGraph.value
        })

        totalCreated++
      }
    }

    return { success: true, created: totalCreated }
  }

  // Assign the given entity schemas (in place) to bounded contexts based on the names
  // the AI put on the raw responses, reusing any bounded contexts already on the workflow
  // and persisting newly created ones.
  const applyBoundedContexts = async (entitySchemas, llmEntities) => {
    const existingBoundedContexts = workflow.value.boundedContexts || []
    const { boundedContexts, entityBcId } = assignBoundedContexts(
      entitySchemas,
      llmEntities,
      workflow.value.name,
      () => crypto.randomUUID(),
      existingBoundedContexts
    )

    for (const entity of entitySchemas) {
      entity.boundedContext = entityBcId.get(entity.id)
    }

    if (boundedContexts.length > existingBoundedContexts.length) {
      await store.dispatch('workflow/updateWorkflow', {
        projectId: workflow.value.projectId,
        input: {
          id: workflow.value.id,
          expectedVersion: workflow.value.version,
          boundedContexts,
          updateNote: {
            action: boundedContexts.length > 1 ? 'added bounded contexts' : 'added bounded context',
            target: boundedContexts.map((bc) => bc.name).join(', ')
          }
        }
      })
    }
  }

  const regenerateEntities = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const response = await generateEntitiesFromWorkflow({
      workflow: workflow.value,
      cardTypes: cardTypes.value
    })

    const entities = response?.entities || []

    if (!entities.length) {
      return { success: false, error: 'Failed to generate entities' }
    }

    let newSchemas = safeStructuredClone(workflow.value.schemas || [])

    const nonEntitySchemas = newSchemas.filter((s) => s.type !== SchemaType.ENTITY)
    const totalDeleted = newSchemas.length - nonEntitySchemas.length
    newSchemas = nonEntitySchemas

    const entityNameToIdMap = new Map()

    const newEntityUUIDs = new Map()
    for (const entity of entities) {
      const uuid = crypto.randomUUID()
      newEntityUUIDs.set(entity.name, uuid)
      entityNameToIdMap.set(entity.name.toLowerCase(), uuid)
    }

    const newEntitySchemas = []
    for (const entityFromLLM of entities) {
      const generatedEntity = parseEntityResponse(entityFromLLM, entityNameToIdMap)
      generatedEntity.id = newEntityUUIDs.get(entityFromLLM.name)
      newEntitySchemas.push(generatedEntity)
    }

    await applyBoundedContexts(newEntitySchemas, entities)

    newSchemas = [...newSchemas, ...newEntitySchemas]

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })

    return { success: true, created: entities.length, deleted: totalDeleted }
  }

  const addMissingEntities = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const response = await generateEntitiesFromWorkflow({
      workflow: workflow.value,
      cardTypes: cardTypes.value
    })

    const entities = response?.entities || []

    if (!entities.length) {
      return { success: false, error: 'Failed to generate entities' }
    }

    let newSchemas = safeStructuredClone(workflow.value.schemas || [])

    const existingEntityNames = new Set(
      newSchemas.filter((s) => s.type === SchemaType.ENTITY).map((s) => s.name.toLowerCase())
    )

    const newEntities = entities.filter((entity) => !existingEntityNames.has(entity.name.toLowerCase()))

    if (newEntities.length === 0) {
      return { success: true, created: 0, skipped: entities.length }
    }

    const entityNameToIdMap = new Map()

    for (const schema of newSchemas) {
      if (schema.type === SchemaType.ENTITY) {
        entityNameToIdMap.set(schema.name.toLowerCase(), schema.id)
      }
    }

    const newEntityUUIDs = new Map()
    for (const entity of newEntities) {
      const uuid = crypto.randomUUID()
      newEntityUUIDs.set(entity.name, uuid)
      entityNameToIdMap.set(entity.name.toLowerCase(), uuid)
    }

    const newEntitySchemas = []
    for (const entityFromLLM of newEntities) {
      const generatedEntity = parseEntityResponse(entityFromLLM, entityNameToIdMap)
      generatedEntity.id = newEntityUUIDs.get(entityFromLLM.name)
      newEntitySchemas.push(generatedEntity)
    }

    await applyBoundedContexts(newEntitySchemas, newEntities)

    newSchemas = [...newSchemas, ...newEntitySchemas]

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })

    return { success: true, created: newEntities.length, skipped: entities.length - newEntities.length }
  }

  const createEntitiesFromDescription = async (args = {}) => {
    const { description, replaceExisting = false } = args

    if (!description) {
      return { success: false, error: 'Description is required' }
    }

    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const response = await generateEntitiesFromDescription({
      workflow: workflow.value,
      cardTypes: cardTypes.value,
      userDescription: description
    })

    const entities = response?.entities || []

    if (!entities.length) {
      return { success: false, error: 'Failed to generate entities from description' }
    }

    let newSchemas = safeStructuredClone(workflow.value.schemas || [])
    let totalDeleted = 0

    if (replaceExisting) {
      const nonEntitySchemas = newSchemas.filter((s) => s.type !== SchemaType.ENTITY)
      totalDeleted = newSchemas.length - nonEntitySchemas.length
      newSchemas = nonEntitySchemas
    }

    const existingEntityNames = new Set(
      newSchemas.filter((s) => s.type === SchemaType.ENTITY).map((s) => s.name.toLowerCase())
    )

    const newEntities = entities.filter((entity) => !existingEntityNames.has(entity.name.toLowerCase()))

    if (newEntities.length === 0 && !replaceExisting) {
      return { success: true, created: 0, skipped: entities.length }
    }

    const entityNameToIdMap = new Map()

    for (const schema of newSchemas) {
      if (schema.type === SchemaType.ENTITY) {
        entityNameToIdMap.set(schema.name.toLowerCase(), schema.id)
      }
    }

    const newEntityUUIDs = new Map()
    for (const entity of newEntities) {
      const uuid = crypto.randomUUID()
      newEntityUUIDs.set(entity.name, uuid)
      entityNameToIdMap.set(entity.name.toLowerCase(), uuid)
    }

    const newEntitySchemas = []
    for (const entityFromLLM of newEntities) {
      const generatedEntity = parseEntityResponse(entityFromLLM, entityNameToIdMap)
      generatedEntity.id = newEntityUUIDs.get(entityFromLLM.name)
      newEntitySchemas.push(generatedEntity)
    }

    await applyBoundedContexts(newEntitySchemas, newEntities)

    newSchemas = [...newSchemas, ...newEntitySchemas]

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })

    if (replaceExisting) {
      return { success: true, created: newEntities.length, deleted: totalDeleted }
    }

    return { success: true, created: newEntities.length, skipped: entities.length - newEntities.length }
  }

  const createCommandFieldsFromDescription = async (args = {}) => {
    const { description, replaceExisting = false } = args

    if (!description) {
      return { success: false, error: 'Description is required' }
    }

    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const commandCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.COMMAND)
    if (!commandCardType) {
      return { success: false, error: 'No command card type found' }
    }

    let schemasForGeneration = workflow.value.schemas || []
    let totalDeleted = 0

    if (replaceExisting) {
      const commandSchemaIds = new Set()
      for (const schema of schemasForGeneration) {
        if (schema.type === SchemaType.COMMAND) {
          commandSchemaIds.add(schema.id)
        }
      }

      const schemasWithoutCommands = schemasForGeneration.filter((s) => s.type !== SchemaType.COMMAND)
      totalDeleted = commandSchemaIds.size

      if (totalDeleted > 0) {
        const currentSvgGraph = safeStructuredClone(svgGraph.value)

        for (const eventId of Object.keys(currentSvgGraph)) {
          const cards = currentSvgGraph[eventId]?.cards || []
          for (const card of cards) {
            if (card.schemaId && commandSchemaIds.has(card.schemaId)) {
              delete card.schemaId
            }
          }
        }

        await workflowApi.updateWorkflow({
          svgGraph: currentSvgGraph,
          svgLanes: svgLanes.value,
          svgGroups: svgGroups.value,
          schemas: schemasWithoutCommands,
          workflow: workflow.value,
          updateNote: {
            action: 'deleted command schemas',
            target: `${totalDeleted} schemas`
          }
        })

        schemasForGeneration = schemasWithoutCommands
      }
    }

    const response = await generateCommandsFromDescription({
      workflow: { ...workflow.value, schemas: schemasForGeneration },
      cardTypes: cardTypes.value,
      userDescription: description,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate command fields from description' }
    }

    let totalCreated = 0

    for (const eventData of events) {
      const event = findEventByResponse(eventData)
      if (!event) {
        continue
      }

      const commandCard = cardHelpers.methods.getCommandCard(event)
      if (!commandCard || (!replaceExisting && commandCard.schemaId) || !eventData.fields?.length) {
        continue
      }

      const fields = parseCommandFields(eventData)

      const newSchema = {
        name: SchemaType.COMMAND,
        type: SchemaType.COMMAND,
        fields
      }

      await workflowApi.addSchemaAndLinkCard({
        schema: newSchema,
        cardId: commandCard.id,
        eventId: event.id,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })

      totalCreated++
    }

    if (replaceExisting) {
      return { success: true, created: totalCreated, deleted: totalDeleted }
    }

    return { success: true, created: totalCreated }
  }

  const createReadModelFieldsFromDescription = async (args = {}) => {
    const { description, replaceExisting = false } = args

    if (!description) {
      return { success: false, error: 'Description is required' }
    }

    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const readModelCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.READ_MODEL)
    if (!readModelCardType) {
      return { success: false, error: 'No read model card type found' }
    }

    let schemasForGeneration = workflow.value.schemas || []
    let totalDeleted = 0

    if (replaceExisting) {
      const querySchemaIds = new Set()
      for (const schema of schemasForGeneration) {
        if (schema.type === SchemaType.QUERY) {
          querySchemaIds.add(schema.id)
        }
      }

      const schemasWithoutQueries = schemasForGeneration.filter((s) => s.type !== SchemaType.QUERY)
      totalDeleted = querySchemaIds.size

      if (totalDeleted > 0) {
        const currentSvgGraph = safeStructuredClone(svgGraph.value)

        for (const eventId of Object.keys(currentSvgGraph)) {
          const cards = currentSvgGraph[eventId]?.cards || []
          for (const card of cards) {
            if (card.schemaId && querySchemaIds.has(card.schemaId)) {
              delete card.schemaId
            }
          }
        }

        await workflowApi.updateWorkflow({
          svgGraph: currentSvgGraph,
          svgLanes: svgLanes.value,
          svgGroups: svgGroups.value,
          schemas: schemasWithoutQueries,
          workflow: workflow.value,
          updateNote: {
            action: 'deleted read model schemas',
            target: `${totalDeleted} schemas`
          }
        })

        schemasForGeneration = schemasWithoutQueries
      }
    }

    const response = await generateReadModelsFromDescription({
      workflow: { ...workflow.value, schemas: schemasForGeneration },
      cardTypes: cardTypes.value,
      userDescription: description,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate read model fields from description' }
    }

    let totalCreated = 0

    for (const eventData of events) {
      const event = findEventByResponse(eventData)
      if (!event || !eventData.readModelFields?.length) {
        continue
      }

      const queryCards = cardHelpers.methods.getQueryCards(event)
      const cardsToProcess = replaceExisting
        ? queryCards
        : queryCards.filter((card) => !card.schemaId)

      if (!cardsToProcess.length) {
        continue
      }

      const fields = parseReadModelFields(eventData.readModelFields)

      const entities = workflow.value.schemas?.filter((s) => s.type === SchemaType.ENTITY) || []
      const queriedEntity = eventData.queriedEntity
        ? entities.find((e) => e.name.toLowerCase() === eventData.queriedEntity.toLowerCase())
        : null

      for (const queryCard of cardsToProcess) {
        const newSchema = {
          name: SchemaType.QUERY,
          type: SchemaType.QUERY,
          cardinality: eventData.cardinality || 'one-to-many',
          fields,
          entityId: queriedEntity?.id || null
        }

        await workflowApi.addSchemaAndLinkCard({
          schema: newSchema,
          cardId: queryCard.id,
          eventId: event.id,
          workflow: workflow.value,
          svgGraph: svgGraph.value
        })

        totalCreated++
      }
    }

    if (replaceExisting) {
      return { success: true, created: totalCreated, deleted: totalDeleted }
    }

    return { success: true, created: totalCreated }
  }

  const createCardsFromDescription = async (args = {}) => {
    const { description, replaceExisting = false } = args

    if (!description) {
      return { success: false, error: 'Description is required' }
    }

    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const enabledCardTypes = cardTypes.value.filter((ct) => ct.generateWithAI)
    if (!enabledCardTypes.length) {
      return { success: false, error: 'No card types have AI generation enabled' }
    }

    const enabledCardTypeIds = new Set(enabledCardTypes.map((ct) => ct.id))
    let totalDeleted = 0

    if (replaceExisting) {
      for (const event of workflow.value.domainEvents || []) {
        const eventCards = svgGraph.value[event.id]?.cards || []
        const cardsToDelete = eventCards.filter((card) => enabledCardTypeIds.has(card.cardTypeId))

        for (const card of cardsToDelete) {
          await workflowApi.deleteCard(workflow.value.id, { cardId: card.id })
          totalDeleted++
        }
      }
    }

    const response = await generateCardsFromDescription({
      workflow: workflow.value,
      cardTypes: cardTypes.value,
      userDescription: description,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate cards from description' }
    }

    let totalCreated = 0

    for (const cardData of events) {
      const event = findEventByResponse(cardData)
      if (!event) {
        continue
      }

      const eventCards = svgGraph.value[event.id]?.cards || []
      const existingCardTypeIds = replaceExisting
        ? new Set()
        : new Set(eventCards.map((card) => card.cardTypeId))

      for (const cardType of enabledCardTypes) {
        if (existingCardTypeIds.has(cardType.id)) {
          continue
        }

        const cardTypeKey = formatCardKey(cardType.title)
        let cardValues = cardData[cardTypeKey]

        if (!cardValues) {
          continue
        }

        if (!Array.isArray(cardValues)) {
          cardValues = [cardValues]
        }

        for (const cardValue of cardValues) {
          const cardText = typeof cardValue === 'object' ? cardValue.name : cardValue
          if (!cardText) {
            continue
          }

          await workflowApi.createCard(workflow.value.id, event.id, {
            cardTypeId: cardType.id,
            description: cardText
          })
          totalCreated++
        }
      }
    }

    if (replaceExisting) {
      return { success: true, created: totalCreated, deleted: totalDeleted }
    }

    return { success: true, created: totalCreated }
  }

  const planWorkflowWithAI = async (args = {}) => {
    const { description } = args

    if (!description) {
      return { success: false, error: 'Description is required' }
    }

    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const response = await generateWorkflowDiagram({
      workflow: {
        ...workflow.value,
        aiDescription: description
      },
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate events from description' }
    }

    return {
      success: true,
      events
    }
  }

  const validateDomainModel = (args = {}) => {
    const { scope = 'all' } = args
    const result = validateWorkflow(workflow.value, scope)

    return {
      success: true,
      ...result
    }
  }

  const analyzeDomainModel = (args = {}) => {
    const { businessContext, focusAreas } = args
    const schemas = workflow.value.schemas || []
    const domainEvents = workflow.value.domainEvents || []
    const boundedContexts = workflow.value.boundedContexts || []
    const lanes = workflow.value.lanes || []

    const entities = schemas
      .filter((s) => s.type === SchemaType.ENTITY)
      .map((entity) => {
        const fields = (entity.fields || []).map((field) => {
          const relatedEntity = field.relatedEntityId
            ? schemas.find((s) => s.id === field.relatedEntityId)
            : null

          return {
            name: field.name,
            dataType: field.dataType || 'string',
            isRequired: (field.tags || []).includes('REQUIRED'),
            cardinality: field.cardinality || 'one-to-one',
            relatedEntity: relatedEntity?.name || null
          }
        })

        return {
          id: entity.id,
          name: entity.name,
          fields,
          fieldCount: fields.length,
          relationshipCount: fields.filter((f) => f.relatedEntity).length
        }
      })

    const commands = []
    const readModels = []
    const aggregates = []

    for (const event of domainEvents) {
      const eventCards = event.cards || []

      for (const card of eventCards) {
        const cardType = card.cardType || (cardTypes.value || []).find((ct) => ct.id === card.cardTypeId)
        const schema = card.schemaId ? schemas.find((s) => s.id === card.schemaId) : null

        if (cardType?.domainModelRole === DomainModelRole.COMMAND) {
          commands.push({
            eventId: event.id,
            eventName: event.description,
            cardId: card.id,
            hasSchema: !!schema,
            schemaFields: schema?.fields?.map((f) => ({
              name: f.name,
              cardinality: f.cardinality,
              isRequired: (f.tags || []).includes('REQUIRED'),
              relatedEntity: f.relatedEntityId
                ? schemas.find((s) => s.id === f.relatedEntityId)?.name
                : null
            })) || []
          })
        }

        if (cardType?.domainModelRole === DomainModelRole.READ_MODEL) {
          const queriedEntity = schema?.entityId
            ? schemas.find((s) => s.id === schema.entityId)
            : null

          readModels.push({
            eventId: event.id,
            eventName: event.description,
            cardId: card.id,
            cardName: card.name,
            cardinality: schema?.cardinality || 'one-to-many',
            hasSchema: !!schema,
            queriedEntity: queriedEntity?.name || null,
            schemaFields: schema?.fields?.map((f) => f.name) || []
          })
        }

        if (cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT) {
          const linkedEntity = card.schemaId
            ? schemas.find((s) => s.id === card.schemaId)
            : null

          aggregates.push({
            eventId: event.id,
            eventName: event.description,
            cardId: card.id,
            linkedEntity: linkedEntity?.name || null
          })
        }
      }
    }

    const workflowFlow = domainEvents.map((event) => {
      const graphNode = svgGraph.value[event.id]
      const lane = lanes.find((l) => l.id === graphNode?.laneId)

      return {
        id: event.id,
        name: event.description,
        type: graphNode?.type || 'bpmn:Task',
        lane: lane?.name || null,
        hasCommand: commands.some((c) => c.eventId === event.id),
        hasReadModel: readModels.some((r) => r.eventId === event.id),
        hasAggregate: aggregates.some((a) => a.eventId === event.id)
      }
    })

    const entityRelationships = []
    for (const entity of entities) {
      for (const field of entity.fields) {
        if (field.relatedEntity) {
          entityRelationships.push({
            from: entity.name,
            to: field.relatedEntity,
            fieldName: field.name,
            cardinality: field.cardinality
          })
        }
      }
    }

    const summary = {
      totalEvents: domainEvents.length,
      totalEntities: entities.length,
      totalCommands: commands.length,
      totalReadModels: readModels.length,
      totalAggregates: aggregates.length,
      totalBoundedContexts: boundedContexts.length,
      totalLanes: lanes.length,
      eventsWithoutCommands: workflowFlow.filter((e) => e.type === 'bpmn:Task' && !e.hasCommand).map((e) => e.name),
      eventsWithoutReadModels: workflowFlow.filter((e) => e.type === 'bpmn:Task' && !e.hasReadModel).map((e) => e.name),
      commandsWithoutSchema: commands.filter((c) => !c.hasSchema).length,
      readModelsWithoutSchema: readModels.filter((r) => !r.hasSchema).length,
      readModelsWithoutQueriedEntity: readModels.filter((r) => !r.queriedEntity).length
    }

    return {
      success: true,
      workflowName: workflow.value.name,
      businessContext: businessContext || null,
      focusAreas: focusAreas || ['relationships', 'naming', 'aggregates', 'flow', 'completeness', 'ddd-patterns'],
      summary,
      entities,
      entityRelationships,
      commands,
      readModels,
      aggregates,
      workflowFlow,
      boundedContexts: boundedContexts.map((bc) => ({
        id: bc.id,
        name: bc.name,
        color: bc.color
      })),
      lanes: lanes.map((l) => l.name)
    }
  }

  const rollbackWorkflow = async (args) => {
    const currentVersion = workflow.value.version
    let targetVersion
    if (typeof args.version === 'number') {
      targetVersion = args.version
    } else {
      const stepsBack = typeof args.stepsBack === 'number' && args.stepsBack >= 1 ? args.stepsBack : 1
      targetVersion = currentVersion - stepsBack
    }

    if (!targetVersion || targetVersion < 1) {
      return {
        success: false,
        error: `Cannot roll back that far. The current version is ${currentVersion}, so there is no earlier version to restore.`
      }
    }

    if (targetVersion >= currentVersion) {
      return {
        success: false,
        error: `Cannot rollback to version ${targetVersion}. Current version is ${currentVersion}. You can only rollback to a previous version.`
      }
    }

    try {
      await store.dispatch('workflow/restorePreviousWorkflowVersion', {
        restoreVersion: targetVersion,
        workflow: workflow.value
      })

      return {
        success: true,
        message: `Rolled back to version ${targetVersion}.`
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to rollback workflow'
      }
    }
  }

  const functions = {
    [functionType.UPDATE_WORKFLOW_NAME]: updateWorkflowName,
    [functionType.CREATE_EVENT]: createEvent,
    [functionType.UPDATE_EVENT]: updateDomainEvent,
    [functionType.DELETE_EVENT]: deleteEvent,
    [functionType.ADD_CONNECTION]: addConnection,
    [functionType.REMOVE_CONNECTION]: removeConnection,
    [functionType.CREATE_LANE]: createLane,
    [functionType.UPDATE_LANE]: updateLane,
    [functionType.DELETE_LANE]: deleteLane,
    [functionType.CREATE_GROUP]: createGroup,
    [functionType.UPDATE_GROUP]: updateGroup,
    [functionType.DELETE_GROUP]: deleteGroup,
    [functionType.CREATE_CARD]: createCard,
    [functionType.DELETE_CARD]: deleteCard,
    [functionType.UPDATE_CARD]: updateCard,
    [functionType.CREATE_ENTITY]: createEntities,
    [functionType.UPDATE_ENTITY]: updateEntities,
    [functionType.DELETE_ENTITY]: deleteEntity,
    [functionType.CREATE_COMMAND]: createCommands,
    [functionType.UPDATE_COMMAND]: updateCommands,
    [functionType.DELETE_COMMAND]: deleteCommand,
    [functionType.CREATE_DOMAIN_EVENT_SCHEMA]: createDomainEventSchemas,
    [functionType.UPDATE_DOMAIN_EVENT_SCHEMA]: updateDomainEventSchemas,
    [functionType.DELETE_DOMAIN_EVENT_SCHEMA]: deleteDomainEventSchema,
    [functionType.CREATE_READ_MODEL]: createReadModels,
    [functionType.UPDATE_READ_MODEL]: updateReadModels,
    [functionType.DELETE_READ_MODEL]: deleteReadModel,
    [functionType.CREATE_BOUNDED_CONTEXT]: createBoundedContext,
    [functionType.UPDATE_BOUNDED_CONTEXT]: updateBoundedContext,
    [functionType.DELETE_BOUNDED_CONTEXT]: deleteBoundedContext,
    [functionType.REGENERATE_CARDS]: regenerateCards,
    [functionType.ADD_MISSING_CARDS]: addMissingCards,
    [functionType.REGENERATE_COMMANDS]: regenerateCommandSchemas,
    [functionType.ADD_MISSING_COMMANDS]: addMissingCommandSchemas,
    [functionType.REGENERATE_READ_MODELS]: regenerateReadModelSchemas,
    [functionType.ADD_MISSING_READ_MODELS]: addMissingReadModelSchemas,
    [functionType.REGENERATE_ENTITIES]: regenerateEntities,
    [functionType.ADD_MISSING_ENTITIES]: addMissingEntities,
    [functionType.GENERATE_ENTITIES_FROM_DESCRIPTION]: createEntitiesFromDescription,
    [functionType.GENERATE_COMMANDS_FROM_DESCRIPTION]: createCommandFieldsFromDescription,
    [functionType.GENERATE_READ_MODELS_FROM_DESCRIPTION]: createReadModelFieldsFromDescription,
    [functionType.GENERATE_CARDS_FROM_DESCRIPTION]: createCardsFromDescription,
    [functionType.PLAN_WORKFLOW_WITH_AI]: planWorkflowWithAI,
    [functionType.VALIDATE_DOMAIN_MODEL]: validateDomainModel,
    [functionType.ANALYZE_DOMAIN_MODEL]: analyzeDomainModel,
    [functionType.ROLLBACK_WORKFLOW]: rollbackWorkflow
  }

  const createAbortError = () => {
    try {
      // Standard DOMException in browsers
      return new DOMException('Aborted', 'AbortError')
    } catch {
      const err = new Error('Aborted')
      err.name = 'AbortError'
      return err
    }
  }

  const shouldStop = (options) => {
    return !!(options?.signal?.aborted || options?.shouldStop?.())
  }

  const withWorkflowApiCancellation = async (options, fn) => {
    if (!options) {
      return await fn()
    }

    const wrap = (methodName, progressText) => {
      const original = workflowApi?.[methodName]
      if (typeof original !== 'function') {
        return null
      }

      workflowApi[methodName] = async (...methodArgs) => {
        if (shouldStop(options)) {
          throw createAbortError()
        }
        if (progressText && typeof options.onProgress === 'function') {
          options.onProgress(progressText)
        }
        // Preserve `this` for static/class-style methods that use `this.*`
        return await original.apply(workflowApi, methodArgs)
      }

      return original
    }

    const originals = {
      deleteCard: wrap('deleteCard', 'Deleting…'),
      createCard: wrap('createCard', 'Creating…'),
      updateCardsWithInput: wrap('updateCardsWithInput', 'Saving card…'),
      deleteCardWithInput: wrap('deleteCardWithInput', 'Deleting…'),
      updateWorkflow: wrap('updateWorkflow', 'Updating workflow…'),
      updateSchemas: wrap('updateSchemas', 'Updating schemas…'),
      deleteEventFromWorkflow: wrap('deleteEventFromWorkflow', 'Deleting…'),
      deleteEventsFromWorkflow: wrap('deleteEventsFromWorkflow', 'Deleting…')
    }

    try {
      if (shouldStop(options)) {
        throw createAbortError()
      }
      return await fn()
    } finally {
      for (const [key, original] of Object.entries(originals)) {
        if (original) {
          workflowApi[key] = original
        }
      }
    }
  }

  const callFunction = async (name, args, options) => {
    if (!functions[name]) {
      return
    }

    return await withWorkflowApiCancellation(options, async () => {
      return await functions[name](args)
    })
  }

  return {
    callFunction
  }
}
